import firebase from 'firebase/compat/app';

interface CreateBarebonesLoanParams {
  userId: string;
  sessionId: string;
  source: number;
  resumeLink: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export async function createBarebonesLoan(
  firestore: firebase.firestore.Firestore,
  params: CreateBarebonesLoanParams
): Promise<void> {
  const { userId, sessionId, source, resumeLink, email, firstName, lastName } = params;

  if (!userId || !sessionId) {
    console.warn('[LendAPICombined] Cannot create bare bones loan: missing userId or sessionId');
    return;
  }

  const loanId = `lendapi-${sessionId}`;
  const now = Date.now();

  const docRef = firestore.collection('loans').doc(loanId);

  // Firestore security rules deny reads of a loan doc that doesn't exist yet
  // (resource is null, so resource.data.userId errors and the read rule's
  // `||` cannot "rescue" it for a non-admin user) — which is the common case
  // on a user's first-ever login. Treat any read failure as "no existing doc
  // we can see" and fall through to the original create/merge behavior; the
  // create/update rules themselves already prevent writing over a loan this
  // user doesn't own.
  let existingData: firebase.firestore.DocumentData | undefined;
  try {
    const existingDoc = await docRef.get();
    existingData = existingDoc.exists ? existingDoc.data() : undefined;
  } catch (error) {
    console.warn('[LendAPICombined] Could not read existing loan before create/merge:', error);
  }
  const alreadySubmitted = existingData?.workflow === 'submitted';

  if (alreadySubmitted) {
    console.log(
      `[LendAPICombined] Loan ${loanId} is already submitted — linking account without regressing workflow`
    );
  }

  await docRef.set(
    {
      id: loanId,
      userId,
      ...(!alreadySubmitted && { workflow: 'draft', currentStep: 'prequal-complete' }),
      lendAPILoan: true,
      lendAPILoanId: sessionId,
      source,
      createdAt: existingData?.createdAt ?? now,
      updatedAt: now,
      resumeLink,
      ...(email && { email }),
      ...(firstName && { firstName }),
      ...(lastName && { lastName }),
    },
    { merge: true }
  );

  console.log(`[LendAPICombined] Bare bones loan created: ${loanId}`);
}
